import { Flex, Tabs, Box, Text, Checkbox } from '@scale/scaleui-radix';
import {
  WebinarError,
  WebinarErrorMessages,
} from 'react-scale/components/WebinarSessions/constants';
import { UserAutocomplete } from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/UserAutocomplete';
import { WorkerTeamAutocomplete } from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/WorkerTeamAutocomplete';
import { SquadAutocomplete } from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/SquadAutocomplete';
import { ProjectQualificationAutocomplete } from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/ProjectQualificationAutocomplete';
import { SingleAudienceAutoComplete } from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/SingleAudienceAutocomplete';
import { UserIdOrEmailInput } from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/UserIdOrEmailInput';
import {
  CohortSelector,
  type CohortOption,
} from 'react-scale/corp/views/CorpProjectGroupManager/apps/Allocation/components/Cohorts/CohortSelector';
import { useState, useCallback, useEffect, useRef } from 'react';
import { ErrorMessage } from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/ErrorMessage';
import { CohortMappingInput } from '@scale/shared/webinarSessions/types';
import { CohortSubscriptionType } from '@scale/cohorts';

enum InviteeTabs {
  WORKER_TEAM = 'workerTeam',
  USERS = 'users',
  QUALIFICATION = 'qualification',
  SQUAD = 'squad',
  AUDIENCE = 'audience',
  COHORTS = 'cohorts',
}

const INVITEE_TABS = [
  { id: InviteeTabs.COHORTS, title: 'Cohorts' },
  { id: InviteeTabs.USERS, title: 'Users' },
  { id: InviteeTabs.WORKER_TEAM, title: 'Worker Team' },
  { id: InviteeTabs.QUALIFICATION, title: 'Qualification' },
  { id: InviteeTabs.SQUAD, title: 'Squad' },
  { id: InviteeTabs.AUDIENCE, title: 'Audience' },
];

interface WebinarInviteeFormProps {
  project: string | null;
  invitedWorkerTeams: string[];
  setInvitedWorkerTeams: (invitedWorkerTeams: string[]) => void;
  invitedUsers: string[];
  setInvitedUsers: (invitedUsers: string[]) => void;
  invitedQualifications: string[];
  setInvitedQualifications: (invitedQualifications: string[]) => void;
  invitedSquads: string[];
  setInvitedSquads: (invitedSquads: string[]) => void;
  audience: string | null;
  setAudience: (audience: string | null) => void;
  audienceBucket: number | null;
  setAudienceBucket: (audienceBucket: number | null) => void;
  excludeEmptyQueueUsers: boolean;
  setExcludeEmptyQueueUsers: (excludeEmptyQueueUsers: boolean) => void;
  cohortMappings: CohortMappingInput[];
  setCohortMappings: (mappings: CohortMappingInput[]) => void;
  webinarErrors: WebinarError[];
}

const WebinarInviteeForm = ({
  project,
  invitedWorkerTeams,
  setInvitedWorkerTeams,
  invitedUsers,
  setInvitedUsers,
  invitedQualifications,
  setInvitedQualifications,
  invitedSquads,
  setInvitedSquads,
  audience,
  setAudience,
  audienceBucket,
  setAudienceBucket,
  excludeEmptyQueueUsers,
  setExcludeEmptyQueueUsers,
  cohortMappings: _cohortMappings,
  setCohortMappings,
  webinarErrors,
}: WebinarInviteeFormProps) => {
  const [selectedInviteeTab, setSelectedInviteeTab] = useState<InviteeTabs>(InviteeTabs.COHORTS);

  const [defaultCohortVersionIds, setDefaultCohortVersionIds] = useState<string[]>([]);
  const [defaultSubscriptionTypeByVersionId, setDefaultSubscriptionTypeByVersionId] = useState<
    Record<string, CohortSubscriptionType>
  >({});

  const hasInitialized = useRef(false);
  useEffect(() => {
    if (_cohortMappings.length === 0 || hasInitialized.current) return;
    hasInitialized.current = true;
    setDefaultCohortVersionIds(
      _cohortMappings.map(m => m.cohortVersionId).filter((id): id is string => !!id),
    );
    const typeMap: Record<string, CohortSubscriptionType> = {};
    for (const m of _cohortMappings) {
      if (m.cohortVersionId) {
        typeMap[m.cohortVersionId] =
          m.subscriptionType === CohortSubscriptionType.Exact
            ? CohortSubscriptionType.Exact
            : CohortSubscriptionType.Latest;
      }
    }
    setDefaultSubscriptionTypeByVersionId(typeMap);
  }, [_cohortMappings]);

  const handleCohortValueChange = useCallback(
    (options: CohortOption[]) => {
      setCohortMappings(
        options.map(opt => ({
          cohortId: opt.cohortId,
          subscriptionType:
            opt.subscriptionType === CohortSubscriptionType.Exact
              ? CohortSubscriptionType.Exact
              : CohortSubscriptionType.Latest,
          cohortVersionId: opt.cohortVersionId,
        })),
      );
    },
    [setCohortMappings],
  );

  return (
    <Flex direction="column" gap="4">
      <Flex direction="column" gap="2">
        <Text as="label" size="2" weight="medium">
          Add Invitees
        </Text>
        <Tabs.Root
          value={selectedInviteeTab}
          onValueChange={(value: string) => setSelectedInviteeTab(value as InviteeTabs)}
        >
          <Tabs.List variant="outline">
            {INVITEE_TABS.map(tab => (
              <Tabs.Trigger key={tab.id} value={tab.id} variant="outline">
                {tab.title}
              </Tabs.Trigger>
            ))}
          </Tabs.List>
          <Box pt="3">
            <Tabs.Content value={InviteeTabs.WORKER_TEAM}>
              <WorkerTeamAutocomplete
                workerTeamIds={invitedWorkerTeams}
                onWorkerTeamIdsChange={setInvitedWorkerTeams}
              />
            </Tabs.Content>
            <Tabs.Content value={InviteeTabs.USERS}>
              <Flex direction="column" gap="2">
                <Flex direction="column" gap="2">
                  <Text as="label" size="2" weight="medium">
                    Search for users
                  </Text>
                  <UserAutocomplete
                    userIds={invitedUsers}
                    onUserIdsChange={setInvitedUsers}
                    showValues={false}
                    limitToScaliens={false}
                    limitToContributors={true}
                  />
                </Flex>
                <Flex direction="column" gap="2">
                  <Text as="label" size="2" weight="medium">
                    Paste in user IDs or emails (must add +worker to username for @scale.com emails)
                  </Text>
                  <UserIdOrEmailInput userIds={invitedUsers} setUsersIds={setInvitedUsers} />
                </Flex>
              </Flex>
            </Tabs.Content>
            <Tabs.Content value={InviteeTabs.QUALIFICATION}>
              {project && project.length > 0 ? (
                <ProjectQualificationAutocomplete
                  projectId={project}
                  qualificationIds={invitedQualifications}
                  onQualificationIdsChange={setInvitedQualifications}
                />
              ) : (
                <Text size="2">
                  Qualifications can only be added if this webinar is associated with a project.
                </Text>
              )}
            </Tabs.Content>
            <Tabs.Content value={InviteeTabs.SQUAD}>
              <SquadAutocomplete squadIds={invitedSquads} onSquadIdsChange={setInvitedSquads} />
            </Tabs.Content>
            <Tabs.Content value={InviteeTabs.AUDIENCE}>
              <SingleAudienceAutoComplete
                audienceId={audience ?? ''}
                onAudienceIdChange={(value: string | null) => {
                  setAudience(value);
                  // Always reset audience bucket when audience is changed
                  setAudienceBucket(null);
                }}
                audienceBucket={audienceBucket}
                setAudienceBucket={setAudienceBucket}
              />
            </Tabs.Content>
            <Tabs.Content value={InviteeTabs.COHORTS}>
              <CohortSelector
                multiple
                defaultCohortVersionIds={defaultCohortVersionIds}
                defaultSubscriptionTypeByVersionId={defaultSubscriptionTypeByVersionId}
                onValueChange={handleCohortValueChange}
                allowExactSubscription
              />
            </Tabs.Content>
          </Box>
        </Tabs.Root>
        {webinarErrors.includes(WebinarError.INVITEES_REQUIRED) && (
          <ErrorMessage text={WebinarErrorMessages[WebinarError.INVITEES_REQUIRED]} />
        )}
        <Text as="label" size="2">
          <Flex as="span" gap="2">
            <Checkbox
              size="2"
              checked={excludeEmptyQueueUsers}
              onCheckedChange={setExcludeEmptyQueueUsers}
            />
            Exclude empty queue users
          </Flex>
        </Text>
      </Flex>
    </Flex>
  );
};

export default WebinarInviteeForm;
